import { EnvironmentProviders, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { MsalBroadcastService, MsalService, MSAL_INSTANCE } from '@azure/msal-angular';
import { BrowserCacheLocation, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { isAuthRedirectResponse, resetInteractiveSignIn, setAuthRedirectError } from './ensure-signed-in';

function authorityFromEnvironment(): string {
  return environment.auth.authorityUrl.replace(/\/oauth2\/v2\.0\/authorize\/?$/i, '');
}

function redirectUriFromWindow(): string {
  return `${window.location.origin}/auth/callback`;
}

export function provideObservabilityAuth(): EnvironmentProviders[] {
  if (!environment.auth.applicationId) {
    return [];
  }

  const application = new PublicClientApplication({
    auth: {
      clientId: environment.auth.applicationId,
      authority: authorityFromEnvironment(),
      redirectUri: redirectUriFromWindow(),
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    }
  });

  return [
    makeEnvironmentProviders([
      { provide: MSAL_INSTANCE, useValue: application },
      MsalService,
      MsalBroadcastService,
      provideAppInitializer(async () => {
        const returningFromAuth = isAuthRedirectResponse();
        await application.initialize();
        resetInteractiveSignIn();
        try {
          const result = await application.handleRedirectPromise();
          resetInteractiveSignIn();
          if (result?.account) {
            application.setActiveAccount(result.account);
            return;
          }

          if (application.getAllAccounts().length > 0) {
            application.setActiveAccount(application.getAllAccounts()[0]);
            return;
          }

          if (returningFromAuth) {
            setAuthRedirectError('Sign-in completed but no account was returned.');
          }
        } catch (error) {
          resetInteractiveSignIn();
          sessionStorage.removeItem('msal.interaction.status');
          setAuthRedirectError(error instanceof Error ? error.message : 'Sign-in failed.');
        }
      })
    ])
  ];
}
